import React, { useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { resetEmailTypes, setEmailTypes, setEmailTypesLoading } from '../../../store/emailHub/emailTypes'
import { resetEmailThreads, selectShouldInitialize } from '../../../store/emailHub/emailThreads'
import { useLazyGetAllExternalUsersQuery } from '../../../store/externalUser/externalUser'
import { useLazyGetAllEmailFiltersQuery } from '../../../store/integrations/emailFilters'
import { useLazyGetAllEmailTypesQuery } from '../../../store/integrations/emailTypes'
import { resetTasks } from '../../../store/task/task'
import { resetEmailMetadata } from '../../../store/emailHub/emailMetadata'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useLazyGetMeQuery } from '../../../store/user/user'
import {
  resetEmailFilters,
  selectDefaultEmailFilter,
  setEmailFilters,
  setEmailFilterSender,
  setEmailFiltersLoading,
  setSelectedQuickEmailFilter,
} from '../../../store/emailHub/emailFilters'
import {
  resetEmailUsers,
  setCurrentUser,
  setEmailCurrentUserLoading,
  setEmailContacts,
  setEmailContactsLoading,
  selectCurrentUserEmailIntegration,
} from '../../../store/emailHub/emailUsers'
import { User } from '../../../models/User'

import { useFetchEmailThread, useFetchEmailThreads, useFetchEmailThreadTasks } from '../hooks'
import { EmailTypeKind } from '../../../models/EmailType'

export interface EmailHubInitializationProps {
  threadId?: string
}

export const EmailHubInitialization: React.FC<EmailHubInitializationProps> = () => {
  useInitializeEmailFilters()
  useInitializeEmailTypes()
  useInitializeEmailUsers()
  useInitializeEmailThreads()
  useInitializeEmailThreadTasks()
  useCleanUpEmailThreads()

  return null
}

export interface ContactEmailsInitializationProps {
  contact: User
}

export const ContactEmailsInitialization: React.FC<ContactEmailsInitializationProps> = ({ contact }) => {
  useInitializeContactFilter(contact)
  useInitializeEmailTypes()
  useInitializeEmailUsers()
  useInitializeContactEmailThreads(contact)
  useInitializeEmailThreadTasks()
  useCleanUpEmailThreads()

  return null
}

const useInitializeEmailFilters = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)

  const [triggerGetAllEmailFilters] = useLazyGetAllEmailFiltersQuery()

  useEffect(() => {
    const fetchEmailFilters = async () => {
      dispatch(setEmailFiltersLoading(true))
      try {
        const { data: emailFilters } = await triggerGetAllEmailFilters({ accountId })
        if (!emailFilters?.data) {
          return
        }
        dispatch(setEmailFilters(emailFilters.data))
        const defaultFilter = emailFilters.data.find((filter) => filter.isDefault)
        if (defaultFilter) {
          dispatch(setSelectedQuickEmailFilter(defaultFilter))
        }
      } finally {
        dispatch(setEmailFiltersLoading(false))
      }
    }
    fetchEmailFilters()
    return () => {
      dispatch(resetEmailFilters())
    }
  }, [dispatch, accountId, triggerGetAllEmailFilters])
}

const useInitializeContactFilter = (contact: User) => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(setEmailFilterSender(contact.email))
    return () => {
      dispatch(resetEmailFilters())
    }
  }, [dispatch, contact])
}

const useInitializeEmailTypes = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)

  const [triggerGetAllEmailTypes] = useLazyGetAllEmailTypesQuery()

  useEffect(() => {
    const fetchEmailTypes = async () => {
      dispatch(setEmailTypesLoading(true))
      try {
        const { data: emailTypes } = await triggerGetAllEmailTypes({ accountId, kind: EmailTypeKind.REPLY })
        if (!emailTypes?.data) {
          return
        }
        dispatch(setEmailTypes(emailTypes.data))
      } finally {
        dispatch(setEmailTypesLoading(false))
      }
    }
    fetchEmailTypes()
    return () => {
      dispatch(resetEmailTypes())
    }
  }, [dispatch, accountId, triggerGetAllEmailTypes])
}

const useInitializeEmailUsers = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)

  const [triggerGetMe] = useLazyGetMeQuery()
  const [triggerGetContacts] = useLazyGetAllExternalUsersQuery()

  useEffect(() => {
    const fetchMe = async () => {
      dispatch(setEmailCurrentUserLoading(true))
      try {
        const { data: currentUser } = await triggerGetMe({ accountId }, true)
        if (!currentUser) {
          return
        }
        dispatch(setCurrentUser(currentUser))
      } finally {
        dispatch(setEmailCurrentUserLoading(false))
      }
    }
    const fetchContacts = async () => {
      dispatch(setEmailContactsLoading(true))
      try {
        const { data: contacts } = await triggerGetContacts({ accountId }, true)
        if (!contacts?.data) {
          return
        }
        const filteredContacts = contacts.data.filter((e) => e.email)
        dispatch(setEmailContacts(filteredContacts))
      } finally {
        dispatch(setEmailContactsLoading(false))
      }
    }
    const fetchEmailUsers = async () => {
      await Promise.all([fetchMe(), fetchContacts()])
    }
    fetchEmailUsers()
    return () => {
      dispatch(resetEmailUsers())
    }
  }, [dispatch, accountId, triggerGetMe, triggerGetContacts])
}

const useInitializeEmailThreads = () => {
  const { threadId } = useParams()
  const dispatch = useAppDispatch()

  const defaultEmailFilter = useAppSelector(selectDefaultEmailFilter)
  const shouldInitialize = useAppSelector((state) => selectShouldInitialize(state, threadId))

  const { fetchEmailThreads } = useFetchEmailThreads()
  const { fetchEmailThread } = useFetchEmailThread()

  useEffect(() => {
    const initializeEmailThread = async () => {
      if (!defaultEmailFilter || !shouldInitialize || !threadId) {
        return
      }
      await fetchEmailThread(threadId)
    }
    initializeEmailThread()
  }, [dispatch, fetchEmailThread, defaultEmailFilter, threadId, shouldInitialize])

  useEffect(() => {
    const initializeEmailThreads = async () => {
      if (!defaultEmailFilter || !shouldInitialize || threadId) {
        return
      }
      await fetchEmailThreads({ filterId: defaultEmailFilter.id })
    }
    initializeEmailThreads()
  }, [dispatch, fetchEmailThreads, defaultEmailFilter, threadId, shouldInitialize])
}

const useInitializeContactEmailThreads = (contact: User) => {
  const dispatch = useAppDispatch()

  const { fetchEmailThreads } = useFetchEmailThreads()

  useEffect(() => {
    const initializeContactEmails = async () => {
      await fetchEmailThreads({ filter: { senders: [contact.email] } })
    }
    initializeContactEmails()
  }, [dispatch, contact.email, fetchEmailThreads])
}

const useInitializeEmailThreadTasks = () => {
  const dispatch = useAppDispatch()
  const emailIntegration = useAppSelector(selectCurrentUserEmailIntegration)

  const { threadId = null } = useParams()

  const { fetchTasks } = useFetchEmailThreadTasks()

  useEffect(() => {
    if (!threadId || !emailIntegration) {
      return
    }
    fetchTasks(emailIntegration.serviceId, threadId)
  }, [dispatch, emailIntegration, fetchTasks, threadId])
}

const useCleanUpEmailThreads = () => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    return () => {
      dispatch(resetEmailThreads())
      dispatch(resetEmailMetadata())
      dispatch(resetTasks())
    }
  }, [dispatch])
}
